import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, Spinner } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { apiClient } from '@/services/apiClient';
import { ConfiguredGoogleProjectMap } from './ConfiguredGoogleProjectMap';
import { validProjectCoordinates, type ProjectCityLocation } from './googleProjectGeocoding';

interface LocationRecord {
  id: string;
  projectNumber: string;
  name: string;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  riskLevel: ProjectCityLocation['riskLevel'];
}

/** Tenant identity keys the entire renderer so transient Google results cannot cross accounts. */
export function GoogleProjectLocations({
  refreshKey,
  compact = false,
  filterIds,
}: {
  refreshKey?: string;
  compact?: boolean;
  filterIds?: string[];
}) {
  const auth = useAuth();
  const identity = `${auth.tenant?.id ?? auth.user?.tenantId ?? ''}:${auth.user?.id ?? ''}`;
  return (
    <ProjectLocationsView
      key={identity}
      refreshKey={refreshKey}
      compact={compact}
      filterIds={filterIds}
    />
  );
}

function ProjectLocationsView({
  refreshKey,
  compact,
  filterIds,
}: {
  refreshKey?: string;
  compact: boolean;
  filterIds?: string[];
}) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<LocationRecord[] | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let canceled = false;
    setError('');
    void apiClient
      .get<{ projects: LocationRecord[] }>('/maps/project-locations')
      .then((result) => {
        if (canceled) return;
        if (!result.ok || !result.data || !Array.isArray(result.data.projects)) {
          setError(
            result.status === 403
              ? 'Your role cannot read project locations.'
              : 'Project locations could not be loaded.',
          );
          return;
        }
        setProjects(result.data.projects);
      })
      .catch(() => {
        if (!canceled) setError('Project locations could not be loaded.');
      });
    return () => {
      canceled = true;
    };
  }, [attempt, refreshKey]);

  if (error)
    return (
      <div className="border-border space-y-3 rounded-lg border p-4" role="alert">
        <p className="text-sm">{error}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAttempt((value) => value + 1)}
        >
          Retry project locations
        </Button>
      </div>
    );
  if (!projects)
    return (
      <div className="flex min-h-[240px] items-center justify-center gap-2 text-sm" role="status">
        <Spinner className="h-4 w-4" />
        Loading project locations…
      </div>
    );
  const visible = filterIds
    ? projects.filter((project) => filterIds.includes(project.id))
    : projects;
  const mapped = visible.filter((project) =>
    validProjectCoordinates(project.latitude, project.longitude),
  );
  const cities = visible
    .filter((project) => !validProjectCoordinates(project.latitude, project.longitude))
    .map((project) => ({ ...project, title: `${project.projectNumber}, ${project.name}` }));

  return (
    <div className="space-y-3">
      <ConfiguredGoogleProjectMap
        markers={mapped.map((project) => ({
          id: project.id,
          title: `${project.projectNumber}, ${project.name}`,
          latitude: project.latitude!,
          longitude: project.longitude!,
          riskLevel: project.riskLevel,
        }))}
        cities={cities}
        onSelect={(id) => {
          void navigate(`/projects/overview?projectId=${encodeURIComponent(id)}`);
        }}
        className={compact ? 'h-[320px]' : undefined}
      />
      {visible.length === 0 ? (
        <p className="text-muted-foreground text-sm">No project location records are available.</p>
      ) : (
        <details className="text-muted-foreground text-xs">
          <summary className="cursor-pointer">
            {visible.length} project records · {mapped.length} with coordinates · {cities.length}{' '}
            need location review
          </summary>
          <ul className="mt-2 max-h-40 space-y-2 overflow-auto">
            {visible.map((project) => (
              <li key={project.id}>
                <Link
                  className="text-primary underline underline-offset-2"
                  to={`/projects/overview?projectId=${encodeURIComponent(project.id)}`}
                >
                  {project.projectNumber}, {project.name}
                </Link>{' '}
                ·{' '}
                {[project.city, project.state].filter(Boolean).join(', ') ||
                  'City and state missing'}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
